import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      navigate("/subscription");
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      toast.error("Payment verification timed out. Please contact support.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        withCredentials: true
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
        toast.success("Welcome to TailorGlam Pro!");
        setTimeout(() => navigate("/dashboard"), 3000);
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        toast.error("Payment session expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="subscription-success-page">
      <div className="text-center max-w-md px-8">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 spinner mx-auto mb-6"></div>
            <h1 className="font-heading text-2xl mb-4">Verifying Payment...</h1>
            <p className="text-gray-600">Please wait while we confirm your subscription.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl mb-4">Welcome to Pro!</h1>
            <p className="text-gray-600 mb-2">
              Your subscription is now active. You have 50 AI generations per month.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl mb-4">Something Went Wrong</h1>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. Please contact support or try again.
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="btn-primary"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
